/**
 * Games Page
 * Interactive English learning games for kids
 */
import { useState } from 'react';
import { UserProgress } from '../services/storage';
import { vocabularyData } from '../data/vocabulary';
import { allPhrasesData } from '../data/phrases-complete';
import WordMatchingGame from '../components/games/WordMatchingGame';
import SentenceScrambleGame from '../components/games/SentenceScrambleGame';
import SpellingGame from '../components/games/SpellingGame';
import MemoryGame from '../components/games/MemoryGame';

interface GamesPageProps {
  user: UserProgress;
}

type GameType = 'menu' | 'matching' | 'scramble' | 'spelling' | 'memory';

export default function GamesPage({ user }: GamesPageProps) {
  const [currentGame, setCurrentGame] = useState<GameType>('menu');
  const [lastScore, setLastScore] = useState(0);

  // Prepare sentence data for scramble game
  const sentences = allPhrasesData.slice(0, 10).map(p => ({
    english: p.phrase,
    chinese: p.meaning
  }));

  const handleGameComplete = (score: number) => {
    setLastScore(score);
    setCurrentGame('menu');
  };

  const games = [
    {
      id: 'matching',
      name: '🧩 单词连连看',
      description: '匹配图片和单词',
      color: 'purple',
      difficulty: '简单',
    },
    {
      id: 'memory',
      name: '🎴 记忆翻牌',
      description: '翻牌找配对，锻炼记忆力',
      color: 'orange',
      difficulty: '简单',
    },
    {
      id: 'scramble',
      name: '🔤 句子排序',
      description: '把单词排成正确的句子',
      color: 'blue',
      difficulty: '中等',
    },
    {
      id: 'spelling',
      name: '📝 拼写挑战',
      description: '听发音拼写出单词',
      color: 'green',
      difficulty: '挑战',
    },
  ];

  if (currentGame === 'matching') {
    return (
      <WordMatchingGame
        words={vocabularyData.slice(0, 20)}
        onComplete={handleGameComplete}
        onExit={() => setCurrentGame('menu')}
      />
    );
  }

  if (currentGame === 'memory') {
    return (
      <MemoryGame
        words={vocabularyData.slice(0, 20)}
        onComplete={handleGameComplete}
        onExit={() => setCurrentGame('menu')}
      />
    );
  }

  if (currentGame === 'scramble') {
    return (
      <SentenceScrambleGame
        sentences={sentences}
        onComplete={handleGameComplete}
        onExit={() => setCurrentGame('menu')}
      />
    );
  }

  if (currentGame === 'spelling') {
    return (
      <SpellingGame
        words={vocabularyData.slice(0, 15)}
        onComplete={handleGameComplete}
        onExit={() => setCurrentGame('menu')}
      />
    );
  }

  // Game menu
  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">🎮 游戏乐园</h1>
        <p className="text-gray-500">通过游戏快乐学英语！</p>
      </div>

      {/* Last score */}
      {lastScore > 0 && (
        <div className="bg-yellow-50 rounded-xl p-4 mb-6 text-center animate-bounce-in">
          <p className="text-lg font-bold text-yellow-700">
            🎉 上次得分：{lastScore} 分！
          </p>
        </div>
      )}

      {/* Game cards */}
      <div className="grid gap-4">
        {games.map(game => (
          <button
            key={game.id}
            onClick={() => setCurrentGame(game.id as GameType)}
            className={`
              p-6 rounded-2xl text-left transition-all hover:scale-105
              bg-${game.color}-100 border-2 border-${game.color}-200
              hover:border-${game.color}-400 hover:shadow-lg
            `}
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-1">
                  {game.name}
                </h3>
                <p className="text-gray-600">{game.description}</p>
              </div>
              <div className="text-right">
                <span className={`
                  px-3 py-1 rounded-full text-sm font-medium
                  ${game.difficulty === '简单' ? 'bg-green-100 text-green-600' :
                    game.difficulty === '中等' ? 'bg-blue-100 text-blue-600' :
                    'bg-orange-100 text-orange-600'}
                `}>
                  {game.difficulty}
                </span>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Tips */}
      <div className="mt-8 bg-gray-50 rounded-xl p-4">
        <p className="text-sm text-gray-500 text-center">
          💡 游戏中遇到困难可以点击"提示"，但会扣掉一些分数哦！
        </p>
      </div>
    </div>
  );
}